import React, { useState } from "react";
import { Download, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { fmtCAD, fmtDate, downloadReceipt } from "@/lib/billing";

const KIND = { subscription: "Subscription", pack: "Credit pack", grant: "Subscription (manual)", refund: "Refund" };
const BY = { renewal: "auto-renewal", retry: "retry", reactivate: "reactivation" };
const STATUS_TONE = {
  paid: "text-emerald-700", failed: "text-rose-700", pending: "text-amber-700",
  refunded: "text-slate-500", partially_refunded: "text-amber-700",
};

export default function TransactionsList({ rows, onRefund, compact = false, testid = "transactions-list" }) {
  const [dl, setDl] = useState("");
  const download = async (t) => {
    setDl(t.id);
    try { await downloadReceipt(t.receipt_id, t.receipt_number); }
    catch (e) { toast.error("Download failed", { description: formatApiError(e) }); }
    finally { setDl(""); }
  };
  return (
    <section className={compact ? "" : "rounded-lg border border-slate-200 bg-white p-5"} data-testid={testid}>
      {!compact ? <h2 className="text-base md:text-lg font-semibold text-slate-900">Transactions & receipts</h2> : null}
      {!rows?.length ? (
        <p className="text-sm text-slate-500 mt-2">No transactions yet.</p>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100">
          {rows.map((t) => (
            <li key={t.id} className="py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" data-testid={`txn-row-${t.id}`}>
              <span className="text-xs text-slate-500 w-24 shrink-0">{fmtDate(t.paid_at || t.created_at)}</span>
              <span className="flex-1 min-w-[160px]">
                <span className="font-medium text-slate-900">{KIND[t.kind] || t.kind}{BY[t.by] ? ` · ${BY[t.by]}` : ""}{t.pack_label ? ` · ${t.pack_label}` : ""}{t.kind !== "pack" && t.months > 1 ? ` · ${t.months} months` : ""}</span>
                <span className="block text-[11px] text-slate-500">
                  {t.method === "card" ? `${t.card_brand || "Card"} •••• ${t.card_last4 || "????"}` : `Manual — ${t.method_note || "other"}`}
                  {t.anet_trans_id ? ` · Authorize.Net ${t.anet_trans_id}` : ""}
                  {t.province ? ` · ${t.province}` : ""}
                  {t.reason ? ` · ${t.reason}` : ""}
                </span>
              </span>
              <span className={"text-xs capitalize " + (STATUS_TONE[t.status] || "text-slate-600")} data-testid={`txn-status-${t.id}`}>{t.status.replace("_", " ")}</span>
              <span className="tabular-nums font-semibold text-slate-900 w-24 text-right" data-testid={`txn-total-${t.id}`}>{fmtCAD(t.total_cad)}</span>
              <span className="flex items-center gap-1">
                {t.receipt_id ? (
                  <Button size="sm" variant="ghost" onClick={() => download(t)} disabled={dl === t.id} title={`Download ${t.receipt_number}`} data-testid={`txn-receipt-${t.id}`}>
                    {dl === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                    <span className="ml-1 text-xs">{t.receipt_number}</span>
                  </Button>
                ) : null}
                {onRefund && (t.status === "paid" || t.status === "partially_refunded") && t.kind !== "refund" ? (
                  <Button size="sm" variant="ghost" className="text-rose-700" onClick={() => onRefund(t)} title="Refund / void" data-testid={`txn-refund-${t.id}`}>
                    <RotateCcw className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
